// ADR 8.9.26g §3 — the bordered box, which was `rounded border border-slate-200
// bg-white p-4` written out at every surface that wanted one.
//
// It takes an element rather than assuming a `<div>`: the same treatment is a
// `<li>` in a list, a `<section>` around a locked affordance and a `<div>`
// around a flashcard, and forcing a wrapper element around each of those would
// change the document outline to keep the styling honest.
//
// ADR 15.9.26af §5 — a `tone`, and a 12px corner (M3 `shapes.medium`). The
// default stays `outlined`, so no page's tone changes here; 15.9.26ai picks
// `tonal` and `raised` surface by surface. What every card gets today is the
// corner.
//
// ADR 30.9.26c §1 — the default is now `tonal`. The page, the groups on it,
// the options in a group and the actions beside them are four things, and one
// grey line on the page colour drew all four. A static group is a
// `surface-container` tint *inside* its edge (the edge is the only cue that
// clears 3:1 against the page, so it stays); a card nested in a filled
// surface asks for `outlined` and steps back to the page colour; an option
// you choose is `raised` and pressable; an action is a `Button`.
//
// ADR 16.9.26t §3 — a pressable card. The phones' `Card(onClick)` is a whole
// tappable surface; on the web that is a `<button>` *around* the card, which
// `styleFloor.test.ts` AC3 allows only in this layer. With `onClick` the card
// renders as `<button type="button">` around a `span`-tagged card — a
// `<button>` permits phrasing content only, so the card is a `span` there —
// carrying `FOCUS_RING` and the full width. `as` is ignored in that form: the
// outer element is the button, and the inner one has to be phrasing content.

import type { ElementType, KeyboardEventHandler, MouseEventHandler, ReactNode } from 'react'

import { FOCUS_RING } from './Button'

export type CardTone = 'outlined' | 'tonal' | 'raised' | 'accent'

// ADR 24.9.26k §2 — every tone carries an edge you can see. The tones still
// differ by fill and elevation; what they share is `--boundary`, >= 3:1 on
// every neutral surface. The tonal fill alone is 1.05:1 off the page.
//
// ADR 9.10.26e §1 — `accent` is the one non-neutral tone: the tonal button's
// fill with an `accent` edge (already measured >= 3:1). Text inside it inherits
// `on-accent-soft`, the only pinned pair on that fill — never `text-ink-muted`.
const EDGE = 'border border-boundary'
const TONE: Record<CardTone, string> = {
  outlined: `${EDGE} bg-surface`, // 8.9.26g's treatment, edge darkened
  tonal: `${EDGE} bg-surface-container`, // Android's surfaceVariant card
  raised: `${EDGE} bg-surface shadow-1`, // Android's elevated plan row
  accent: 'border border-accent bg-accent-soft text-on-accent-soft', // a door we want found
}

export interface CardProps {
  /** `div` unless the surrounding markup needs something with meaning. */
  as?: ElementType
  tone?: CardTone
  className?: string
  children: ReactNode
  /** Passed through so a caller's suite can still scope to its own card. */
  'data-testid'?: string
  /** Passed through: `Panel` names its `<section>` landmark with it (24.9.26k §4). */
  'aria-labelledby'?: string
  /**
   * ADR 29.9.26ao §2 — passed through, so an outline can scroll to the card.
   * With `onClick` it lands on the outer `<button>`, like the two above.
   */
  id?: string
  /** ADR 16.9.26t §3 — given, the whole card is a `<button>` (see the header). */
  onClick?: MouseEventHandler<HTMLButtonElement>
  /** ADR 30.9.26c §1 — lands on the pressable `<button>` only; ignored otherwise. */
  disabled?: boolean
  /** ADR 30.9.26c §4 — lands on the pressable `<button>` only; ignored otherwise. */
  'aria-current'?: boolean | 'true' | 'false'
  /**
   * ADR 1.10.26c §6 — the four a radio row needs. Like `disabled`, they land
   * on the pressable `<button>` only and are ignored otherwise.
   */
  role?: string
  'aria-checked'?: boolean
  tabIndex?: number
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>
}

/**
 * ADR 29.9.26aq §5 — a card that is a target lifts on hover: `ListRow`'s
 * card-tone lift keyed to a `group`, so the element that *is* the target (the
 * pressable `<button>`, or a `<Link>` around the card) lights the face inside it.
 * Containers never carry it — a hover there would promise a click that does nothing.
 */
export const CARD_LIFT = 'group-hover:shadow-2 motion-safe:transition-shadow'

// ADR 30.9.26c §1 — a disabled pressable card looks disabled, as a disabled
// `Button` does, and does not lift: a card that cannot be pressed does not
// promise a press.
const PRESSABLE = `group block w-full rounded-card text-left ${FOCUS_RING} disabled:opacity-50`

export function Card({
  as: Tag = 'div',
  tone = 'tonal',
  className = '',
  children,
  onClick,
  disabled,
  'aria-current': ariaCurrent,
  role,
  'aria-checked': ariaChecked,
  tabIndex,
  onKeyDown,
  ...rest
}: CardProps) {
  const face = `rounded-card p-4 ${TONE[tone]} ${className}`
  if (onClick !== undefined) {
    const lift = disabled === true ? '' : ` ${CARD_LIFT}`
    return (
      <button
        type="button"
        className={PRESSABLE}
        onClick={onClick}
        disabled={disabled}
        aria-current={ariaCurrent}
        role={role}
        aria-checked={ariaChecked}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        {...rest}
      >
        <span className={`block ${face}${lift}`}>{children}</span>
      </button>
    )
  }
  return (
    <Tag className={face} {...rest}>
      {children}
    </Tag>
  )
}
